/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/unbound-method */
import { PayloadAction } from '@reduxjs/toolkit';
import {
  call, delay, fork, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';

import { selectPersonas } from 'Selector/PersonasSelector';
import { actions } from 'Slice/PersonasSlice';
import { actions as helperActions } from 'Slice/HelperSlice';
import {
  ConfirmPersonaRequest, ConfirmPersonaResponse, DeleteJobTitleRequest, DeleteJobTitleResponse,
  DeletePersonaRequest, DeletePersonaResponse, DuplicateCheckRequest, DuplicateCheckResponse,
  GeneratedPersona, GeneratePersonaRequest, GeneratePersonaResponse,
  GenerateImportPersonaRequest, GenerateImportPersonaResponse,
  ImportPersonaCompaniesRequest, ImportPersonaCompaniesResponse,
  PersonaImportStatusRequest, PersonaImportStatusResponse,
  ListPersonasRequest,
  ListPersonasResponse, Persona, PersonaCompany, PersonaDetailsRequest, PersonaDetailsResponse,
  PersonaPreview, PersonaProspect, PersonaResultCountRequest, PersonaResultCountResponse,
  PersonaResultsRequest, PersonaResultsResponse, PersonaTitle, RawProspect,
  RawJobTitle, RawPersona, RawPersonaDetails, RawPreviewPerson,
  ReorderJobTitlesRequest, ReorderJobTitlesResponse,
  UpdateJobTitleRequest, UpdateJobTitleResponse,
} from 'Types/PersonasTypes';
import axios from 'Utils/Axios';
import {
  clearPendingPersonaImport, getPendingPersonaImport, PendingPersonaImport,
  setPendingPersonaImport,
} from 'Utils/personaImportStorage';
import {
  API, PERSONA_DELETE_FAILED, PERSONA_IMPORT_FAILED,
  PERSONA_IMPORT_GENERATE_FAILED,
  PERSONA_IMPORT_POLL_INTERVAL, PERSONA_IMPORT_POLL_MAX_FAILURES,
  PERSONA_IMPORT_STATUS_CHECK_FAILED, PERSONA_IMPORT_STATUS_COMPLETED,
  PERSONA_IMPORT_STATUS_FAILED, PERSONA_IMPORT_GENERATION_FAILED, PERSONA_NAME_CHECK_FAILED,
  PERSONA_PROSPECTS_PER_PAGE,
  PERSONA_SAVE_FAILED, PERSONA_SAVED_SUCCESS, PERSONA_TITLE_DELETE_FAILED,
} from 'Utils/Constants';

const toValueList = (raw: string[] | string | undefined): string[] => {
  const entries = Array.isArray(raw) ? raw : [raw ?? ''];
  return entries
    .flatMap((entry) => {
      const value = String(entry ?? '').trim();
      return value.includes('","') ? value.split('","') : [value];
    })
    .map((entry) => entry.replace(/^"+|"+$/g, '').trim())
    .filter(Boolean);
};

const toActive = (raw: RawJobTitle['isActive']): boolean => {
  if (raw === undefined || raw === null) return true;
  const value = String(raw).toLowerCase();
  return value !== 'false' && value !== '0';
};

const toDetailTitles = (
  raw: (RawJobTitle | string)[] | undefined,
  personaId: string,
): PersonaTitle[] => [...raw ?? []]
  .map((entry, index) => (typeof entry === 'string'
    ? {
      id: '', title: entry, order: index + 1, enabled: true,
    }
    : {
      id: String(entry?.jobTitleId ?? entry?.id ?? ''),
      title: entry?.title ?? '',
      order: entry?.order ?? index + 1,
      enabled: toActive(entry?.isActive),
    }))
  .sort((a, b) => a.order - b.order)
  .map((entry, index) => ({
    id: entry.id || `${personaId}-${index}`, name: entry.title, enabled: entry.enabled,
  }));

const toProspects = (raw: RawProspect[] | undefined): PersonaProspect[] => (raw ?? [])
  .map((person, index) => ({
    id: String(person?.personId ?? index),
    name: person?.personName ?? '',
    image: person?.personImage ?? '',
    designation: person?.personDesignation ?? '',
    companyName: person?.companyName ?? '',
    companyLogo: person?.companyLogo ?? '',
    personalityTypes: person?.personalityTypes ?? [],
    archeTypeName: person?.archeType?.name ?? '',
    archeTypeColor: person?.archeType?.color ?? '',
    location: person?.location ?? '',
    employees: Number(person?.employees ?? 0),
    industries: toValueList(person?.industry),
    socialMedia: person?.socialMedia ?? '',
    personAdded: person?.personAdded ?? false,
    personType: person?.personType || person?.executiveType || 'EXECUTIVE',
  }));

const toPreviewPersons = (raw: RawPreviewPerson[] | undefined): PersonaPreview[] => (raw ?? [])
  .map((person, index) => ({
    id: String(person?.personId ?? index),
    name: person?.personName ?? '',
    image: person?.personImage ?? '',
    logo: person?.companyLogo ?? '',
    personalityTypes: person?.personalityTypes ?? [],
    archeTypeName: person?.archeType?.name ?? '',
    archeTypeColor: person?.archeType?.color ?? '',
  }));

const toCompanies = (raw: PersonaCompany[] | undefined): PersonaCompany[] => (raw ?? [])
  .map((company) => ({
    companyId: String(company?.companyId ?? ''),
    companyName: company?.companyName ?? '',
  }))
  .filter((company) => company.companyId !== '');

const saveToast = (message: string, success: boolean) => helperActions.setNotification({
  message,
  proTip: false,
  rightPlacement: false,
  groupId: 0,
  classnameFlag: success,
});

const toPersona = (raw: RawPersona): Persona => ({
  id: String(raw?.personaId ?? raw?.id ?? ''),
  name: raw?.personaName ?? raw?.name ?? '',
  previewPersons: toPreviewPersons(raw?.previewPersons),
});

export function* getPersonas(dispatched: PayloadAction<ListPersonasRequest>) {
  const page = dispatched.payload?.page && dispatched.payload.page > 1
    ? dispatched.payload.page : 1;
  try {
    const searchText = dispatched.payload?.searchText?.trim();
    const response: ListPersonasResponse = yield call(
      axios.post,
      API.LIST_PERSONAS,
      { page, ...searchText ? { searchText } : {} },
    );
    if (response?.status === true) {
      const raw = response.response?.personas ?? [];
      const personaCount = response.response?.personaCount ?? 0;
      let loaded = raw.length;
      if (page > 1) {
        const previous: Persona[] = yield select(selectPersonas);
        loaded += previous.length;
      }
      yield put(actions.setPersonas({
        personaCount,
        personas: raw.map(toPersona),
        page,
        ended: !raw.length || (personaCount > 0 && loaded >= personaCount),
      }));
    } else {
      yield put(actions.getPersonasFailed(page));
    }
  } catch (err) {
    yield put(actions.getPersonasFailed(page));
  }
}

const errorMessage = (err: unknown) => (err as { message?: string })?.message
  || PERSONA_NAME_CHECK_FAILED;

export function* checkPersonaName(dispatched: PayloadAction<DuplicateCheckRequest>) {
  const { personaName } = dispatched.payload;
  try {
    const response: DuplicateCheckResponse = yield call(
      axios.post,
      API.PERSONA_DUPLICATE_CHECK,
      { personaName },
    );
    if (response?.status === true) {
      yield put(actions.personaNameAvailable(personaName));
    } else {
      yield put(actions.checkPersonaNameFailed(response?.message || PERSONA_NAME_CHECK_FAILED));
    }
  } catch (err) {
    yield put(actions.checkPersonaNameFailed(errorMessage(err)));
  }
}

export function* deletePersona(dispatched: PayloadAction<DeletePersonaRequest>) {
  const { personaId } = dispatched.payload;
  try {
    const response: DeletePersonaResponse = yield call(
      axios.post,
      API.DELETE_PERSONA,
      { personaId },
    );
    if (response?.status === true) {
      yield put(actions.personaDeleted(personaId));
    } else {
      yield put(actions.deletePersonaFailed());
      yield put(saveToast(PERSONA_DELETE_FAILED, false));
    }
  } catch (err) {
    yield put(actions.deletePersonaFailed());
    yield put(saveToast(PERSONA_DELETE_FAILED, false));
  }
}

export function* generatePersona(dispatched: PayloadAction<GeneratePersonaRequest>) {
  try {
    const {
      personaName, companyName, productService, companyDescription, industries, personaId,
    } = dispatched.payload;
    const response: GeneratePersonaResponse = yield call(
      axios.post,
      API.GENERATE_PERSONA,
      {
        personaName,
        companyName,
        productService,
        ...companyDescription ? { companyDescription } : {},
        ...toValueList(industries).length ? { industries: toValueList(industries) } : {},
        ...personaId ? { personaId, regenerate: 1 } : {},
      },
    );
    const generated: GeneratedPersona = response?.response ?? response;
    if (response?.jobTitlesNotGenerated || !generated?.jobTitles) {
      yield put(actions.generatePersonaFailed());
      if (response?.message) yield put(saveToast(response.message, false));
    } else {
      const generatedId = String(generated.personaId ?? '');
      yield put(actions.setGeneratedPersona({
        personaId: generatedId,
        personaName: generated.personaName ?? personaName,
        titles: toDetailTitles(generated.jobTitles, generatedId),
      }));
    }
  } catch (err) {
    const failure = (err as { message?: string })?.message;
    yield put(actions.generatePersonaFailed());
    if (failure) yield put(saveToast(failure, false));
  }
}

export function* getPersonaDetails(dispatched: PayloadAction<PersonaDetailsRequest>) {
  const { personaId } = dispatched.payload;
  try {
    const response: PersonaDetailsResponse = yield call(
      axios.post,
      API.PERSONA_DETAILS,
      { personaId },
    );
    if (response?.status === false || response?.success === false) {
      yield put(actions.getPersonaDetailsFailed());
      return;
    }
    const card: RawPersonaDetails = response?.response ?? response;
    const id = String(card?.personaId ?? card?.id ?? personaId);
    yield put(actions.setPersonaDetails({
      id,
      name: card?.personaName ?? card?.name ?? '',
      titles: toDetailTitles(card?.jobTitles, id),
      companyName: card?.companyName ?? '',
      productService: card?.productService ?? '',
      companyDescription: card?.companyDescription ?? '',
      industries: toValueList(card?.industries),
      regions: toValueList(card?.regions),
      excludeKeywords: toValueList(card?.excludeKeywords),
      companies: toCompanies(card?.companies),
    }));
  } catch (err) {
    yield put(actions.getPersonaDetailsFailed());
  }
}

export function* deleteJobTitle(dispatched: PayloadAction<DeleteJobTitleRequest>) {
  const { personaId, jobTitleId } = dispatched.payload;
  try {
    const response: DeleteJobTitleResponse = yield call(
      axios.post,
      API.DELETE_JOB_TITLE,
      { personaId, jobTitleId },
    );
    if (response?.status === false || response?.success === false) {
      yield put(actions.deleteJobTitleFailed(jobTitleId));
      yield put(saveToast(PERSONA_TITLE_DELETE_FAILED, false));
      return;
    }
    yield put(actions.jobTitleDeleted({ personaId, jobTitleId }));
  } catch (err) {
    yield put(actions.deleteJobTitleFailed(jobTitleId));
    yield put(saveToast(PERSONA_TITLE_DELETE_FAILED, false));
  }
}

export function* updateJobTitle(dispatched: PayloadAction<UpdateJobTitleRequest>) {
  const {
    personaId, jobTitleId, title, isActive,
  } = dispatched.payload;
  const revert = isActive === undefined ? {} : { enabled: !isActive };
  try {
    const response: UpdateJobTitleResponse = yield call(
      axios.post,
      API.UPDATE_JOB_TITLE,
      {
        personaId,
        jobTitleId,
        ...title ? { title } : {},
        ...isActive === undefined ? {} : { isActive: isActive ? 'true' : 'false' },
      },
    );
    if (response?.status === false || response?.success === false) {
      yield put(actions.updateJobTitleFailed({ personaId, jobTitleId, ...revert }));
      return;
    }
    const card = response?.response?.jobTitle ?? response?.response ?? response?.jobTitle;
    yield put(actions.jobTitleUpdated({
      personaId,
      jobTitleId,
      name: card?.title || title,
      enabled: card?.isActive === undefined ? isActive : toActive(card.isActive),
    }));
  } catch (err) {
    yield put(actions.updateJobTitleFailed({ personaId, jobTitleId, ...revert }));
  }
}

export function* reorderJobTitles(dispatched: PayloadAction<ReorderJobTitlesRequest>) {
  const { personaId, jobTitleIds } = dispatched.payload;
  try {
    const response: ReorderJobTitlesResponse = yield call(
      axios.post,
      API.REORDER_JOB_TITLES,
      { personaId, jobTitleIds },
    );
    if (response?.status === false || response?.success === false) {
      yield put(actions.getPersonaDetails({ personaId }));
      return;
    }
    const result = response?.response ?? response;
    yield put(actions.jobTitlesReordered({
      personaId,
      titles: toDetailTitles(result?.jobTitles, personaId),
    }));
  } catch (err) {
    yield put(actions.getPersonaDetails({ personaId }));
  }
}

export function* getPersonaProspects(dispatched: PayloadAction<PersonaResultsRequest>) {
  const { personaId, page } = dispatched.payload;
  const current = page && page > 1 ? page : 1;
  try {
    const response: PersonaResultsResponse = yield call(
      axios.post,
      API.PERSONA_RESULTS,
      { personaId, page: current, limit: PERSONA_PROSPECTS_PER_PAGE },
    );
    if (response?.status === false || response?.success === false) {
      yield put(actions.getPersonaProspectsFailed(current));
      return;
    }
    const result = response?.response ?? response;
    const prospects = toProspects(result?.prospects);
    const resultCount = result?.resultCount ?? 0;
    yield put(actions.setPersonaProspects({
      resultCount,
      page: current,
      prospects,
      ended: prospects.length < PERSONA_PROSPECTS_PER_PAGE
        || (resultCount > 0 && current * PERSONA_PROSPECTS_PER_PAGE >= resultCount),
    }));
  } catch (err) {
    yield put(actions.getPersonaProspectsFailed(current));
  }
}

export function* getPersonaResultCount(dispatched: PayloadAction<PersonaResultCountRequest>) {
  const {
    designation, region, industries, excludeKeywords, personaCompanyIds,
  } = dispatched.payload;
  if (!designation.length) {
    yield put(actions.setPersonaResultCount({ totalCount: 0, listStatus: '', listDescription: '' }));
    return;
  }
  try {
    const response: PersonaResultCountResponse = yield call(
      axios.post,
      API.ADVANCE_SEARCH,
      {
        searchType: 'person',
        screenName: 'persona',
        designation,
        ...region?.length ? { region: region.join(',') } : {},
        ...industries?.length ? { industry: industries } : {},
        ...excludeKeywords?.length ? { excludeKeywords } : {},
        ...personaCompanyIds?.length ? { personaCompanyIds } : {},
      },
    );
    if (response?.status === true) {
      const people = (response.searchResult ?? []).find(
        (section) => section?.type === 'Executives',
      );
      yield put(actions.setPersonaResultCount({
        totalCount: people?.totalCount ?? 0,
        listStatus: people?.listStatus ?? '',
        listDescription: people?.listDescription ?? '',
      }));
    } else {
      yield put(actions.getPersonaResultCountFailed());
    }
  } catch (err) {
    yield put(actions.getPersonaResultCountFailed());
  }
}

export function* confirmPersona(dispatched: PayloadAction<ConfirmPersonaRequest>) {
  const {
    personaId, jobTitles, industries, regions, excludeKeywords, isEdit, updatedEntities,
  } = dispatched.payload;
  try {
    const response: ConfirmPersonaResponse = yield call(
      axios.post,
      API.CONFIRM_PERSONA,
      {
        personaId,
        jobTitles: toValueList(jobTitles),
        ...toValueList(industries).length ? { industries: toValueList(industries) } : {},
        ...toValueList(regions).length ? { regions: toValueList(regions) } : {},
        ...toValueList(excludeKeywords).length
          ? { excludeKeywords: toValueList(excludeKeywords) } : {},
        ...updatedEntities?.length ? { updatedEntities } : {},
      },
    );
    if (response?.success === false || response?.status === false) {
      const failure = response?.message || PERSONA_SAVE_FAILED;
      yield put(actions.confirmPersonaFailed(failure));
      if (isEdit) yield put(saveToast(failure, false));
    } else {
      const card: RawPersona = response?.response ?? response;
      yield put(actions.setConfirmedPersona({
        id: String(card?.personaId ?? card?.id ?? personaId),
        name: card?.personaName ?? card?.name ?? '',
        previewPersons: toPreviewPersons(card?.previewPersons),
      }));
      if (isEdit) yield put(saveToast(PERSONA_SAVED_SUCCESS, true));
    }
  } catch (err) {
    const failure = (err as { message?: string })?.message || PERSONA_SAVE_FAILED;
    yield put(actions.confirmPersonaFailed(failure));
    if (isEdit) yield put(saveToast(failure, false));
  }
}

export function* importPersonaCompanies(
  dispatched: PayloadAction<ImportPersonaCompaniesRequest>,
) {
  const { file } = dispatched.payload;
  try {
    const response: ImportPersonaCompaniesResponse = yield call(
      axios.post,
      API.IMPORT_PERSONA_COMPANIES,
      { file },
    );
    const imported = response?.response;
    if (response?.status === true && imported) {
      yield put(actions.setImportedPersona({
        importId: imported.importId,
        fileName: imported.fileName,
      }));
    } else {
      yield put(actions.importPersonaCompaniesFailed());
      yield put(saveToast(response?.message || PERSONA_IMPORT_FAILED, false));
    }
  } catch (err) {
    const failure = (err as { message?: string })?.message || PERSONA_IMPORT_FAILED;
    yield put(actions.importPersonaCompaniesFailed());
    yield put(saveToast(failure, false));
  }
}

export function* generateImportPersona(dispatched: PayloadAction<GenerateImportPersonaRequest>) {
  const {
    importId, personaName, companyName, productService, companyDescription, personaId,
  } = dispatched.payload;
  try {
    const response: GenerateImportPersonaResponse = yield call(
      axios.post,
      API.GENERATE_IMPORT_PERSONA,
      {
        importId,
        personaName,
        companyName,
        productService,
        ...companyDescription ? { companyDescription } : {},
        ...personaId ? { personaId, regenerate: 1 } : {},
      },
    );
    if (response?.status === true && response?.response) {
      yield call(setPendingPersonaImport, importId);
      yield put(actions.importPersonaGenerationStarted({ importId }));
      yield put(actions.pollImportPersonaStatus({ importId }));
    } else {
      yield put(actions.generateImportPersonaFailed());
      yield put(saveToast(response?.message || PERSONA_IMPORT_GENERATE_FAILED, false));
    }
  } catch (err) {
    const failure = (err as { message?: string })?.message || PERSONA_IMPORT_GENERATE_FAILED;
    yield put(actions.generateImportPersonaFailed());
    yield put(saveToast(failure, false));
  }
}

function* stopImportPolling(status: string, message: string) {
  yield call(clearPendingPersonaImport);
  yield put(actions.setImportPersonaStatus(status));
  yield put(saveToast(message, false));
}

export function* pollImportPersonaStatus(dispatched: PayloadAction<PersonaImportStatusRequest>) {
  const { importId } = dispatched.payload;
  let failures = 0;
  while (failures < PERSONA_IMPORT_POLL_MAX_FAILURES) {
    yield delay(PERSONA_IMPORT_POLL_INTERVAL);
    try {
      const response: PersonaImportStatusResponse = yield call(
        axios.post,
        API.PERSONA_IMPORT_STATUS,
        { importId },
      );
      failures = 0;
      const result = response?.response;
      if (response?.status !== true || !result) {
        yield call(
          stopImportPolling,
          PERSONA_IMPORT_STATUS_FAILED,
          response?.message || PERSONA_IMPORT_STATUS_CHECK_FAILED,
        );
        return;
      }
      if (result.status === PERSONA_IMPORT_STATUS_FAILED) {
        yield call(
          stopImportPolling,
          PERSONA_IMPORT_STATUS_FAILED,
          PERSONA_IMPORT_GENERATION_FAILED,
        );
        return;
      }
      if (result.status === PERSONA_IMPORT_STATUS_COMPLETED) {
        yield call(clearPendingPersonaImport);
        yield put(actions.setImportPersonaStatus(PERSONA_IMPORT_STATUS_COMPLETED));
        return;
      }
    } catch (err) {
      failures += 1;
    }
  }
  yield call(stopImportPolling, PERSONA_IMPORT_STATUS_FAILED, PERSONA_IMPORT_STATUS_CHECK_FAILED);
}

export function* resumePendingPersonaImport() {
  const pending: PendingPersonaImport | null = yield call(getPendingPersonaImport);
  if (!pending) return;
  yield put(actions.resumeImportPersonaStatus({ importId: pending.importId }));
  yield put(actions.pollImportPersonaStatus({ importId: pending.importId }));
}

export function* personasSaga() {
  yield fork(resumePendingPersonaImport);
  yield takeLatest(actions.getPersonas.type, getPersonas);
  yield takeLatest(actions.checkPersonaName.type, checkPersonaName);
  yield takeLatest(actions.deletePersona.type, deletePersona);
  yield takeLatest(actions.generatePersona.type, generatePersona);
  yield takeLatest(actions.confirmPersona.type, confirmPersona);
  yield takeLatest(actions.getPersonaDetails.type, getPersonaDetails);
  yield takeLatest(actions.getPersonaResultCount.type, getPersonaResultCount);
  yield takeEvery(actions.deleteJobTitle.type, deleteJobTitle);
  yield takeEvery(actions.updateJobTitle.type, updateJobTitle);
  yield takeLatest(actions.reorderJobTitles.type, reorderJobTitles);
  yield takeLatest(actions.getPersonaProspects.type, getPersonaProspects);
  yield takeLatest(actions.importPersonaCompanies.type, importPersonaCompanies);
  yield takeLatest(actions.generateImportPersona.type, generateImportPersona);
  yield takeLatest(actions.pollImportPersonaStatus.type, pollImportPersonaStatus);
}
